import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useRef, useState } from 'react';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { capabilityHooks } from '../../Registries/hooks';
import { PROGRAM_WRITE_DENIED, useProgramWriteAccess } from '../programWriteAccess';
import { programCapabilityHooks } from '../relationshipHooks';
// Sentinel for the "create a brand-new capability" option in the dropdown.
const NEW_CAPABILITY = '__new__';
const EMPTY_FORM = {
    capability: '',
    newName: '',
    description: '',
    percentage: ''
};
const columns = [
    {
        key: 'category',
        header: 'Category'
    },
    {
        key: 'percentage',
        header: '% of effort',
        render: (row)=>`${row.percentage}%`
    },
    {
        key: 'description',
        header: 'Description'
    }
];
const capEditDefaults = (c)=>({
        category: c.category ?? '',
        percentage: c.percentage ?? 0,
        description: c.description ?? ''
    });
function capabilityDetailSections(c) {
    return [
        {
            heading: 'Capability',
            rows: [
                {
                    label: 'Category',
                    value: c.category || '—',
                    editField: {
                        kind: 'text',
                        name: 'category',
                        label: 'Category',
                        required: true
                    }
                },
                {
                    label: '% of effort',
                    value: `${c.percentage}%`,
                    editField: {
                        kind: 'number',
                        name: 'percentage',
                        label: 'Percentage (0-100)',
                        min: 0,
                        max: 100,
                        step: 1
                    }
                }
            ]
        },
        {
            heading: 'Description',
            rows: [
                {
                    label: 'Description',
                    value: c.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                }
            ]
        }
    ];
}
function LabeledField({ id, label, required, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsxs(Label, {
                htmlFor: id,
                children: [
                    label,
                    required && /*#__PURE__*/ _jsx("span", {
                        className: "ml-1 text-destructive",
                        children: "*"
                    })
                ]
            }),
            children
        ]
    });
}
export function ProgramCapabilitiesTab({ programId }) {
    // Upsert/DeleteProgramCapability: update + area:programs. canEdit is the
    // update verb alone, so a Quality (or contracts) role with edit but no
    // roster area would see a button the server refuses.
    const canWrite = useProgramWriteAccess(programId).roster;
    const tenantId = useTenantId();
    const registryQuery = capabilityHooks.useList({
        companyId: tenantId
    });
    const capabilityUpsert = capabilityHooks.useUpsert();
    const { data, isLoading } = programCapabilityHooks.useList({
        programId
    });
    const upsert = programCapabilityHooks.useUpsert();
    const remove = programCapabilityHooks.useDelete();
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    // Store only the selected id and DERIVE the record from `items` — an open
    // drawer reflects the latest list after an inline-edit Save without a re-sync
    // effect (which infinite-loops on fresh-but-equal refs each render).
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Holds a registry capability created earlier in THIS dialog session so a
    // retry after a failed program attach reuses it rather than creating a second.
    const createdCapabilityIdRef = useRef('');
    const registry = useMemo(()=>registryQuery.data?.items ?? [], [
        registryQuery.data?.items
    ]);
    // Keyed on name: the registry is deduped by name (ryugu UpsertCapability), so
    // names are assumed unique here. If a race ever lands a tenant row alongside a
    // _system row of the same name, this map intentionally collapses them to one
    // dropdown entry rather than showing a confusing duplicate.
    const registryByName = useMemo(()=>{
        const m = new Map();
        for (const c of registry)m.set(c.name, c);
        return m;
    }, [
        registry
    ]);
    const isNewCapability = form.capability === NEW_CAPABILITY;
    const setField = (key, value)=>setForm((prev)=>({
                ...prev,
                [key]: value
            }));
    // Picking an existing capability pre-fills the description from the registry
    // (still editable); picking "+ New" clears it for typing.
    const selectCapability = (value)=>{
        createdCapabilityIdRef.current = '';
        if (value === NEW_CAPABILITY) {
            setForm((prev)=>({
                    ...prev,
                    capability: value,
                    newName: '',
                    description: ''
                }));
            return;
        }
        setForm((prev)=>{
            const reg = registryByName.get(value);
            return {
                ...prev,
                capability: value,
                // Pre-fill the description from the registry when a catalog entry is
                // picked; a legacy value (not in the registry) keeps the program's own
                // note rather than wiping it to empty.
                description: reg ? reg.description : prev.description
            };
        });
    };
    const openAdd = ()=>{
        setForm(EMPTY_FORM);
        createdCapabilityIdRef.current = '';
        setFormOpen(true);
    };
    // Capabilities partition a program's effort, so the total across all of them
    // can be at most 100%. This is a fast-feedback mirror of the AUTHORITATIVE
    // guard: ryugu UpsertProgramCapability re-validates and pangaea's
    // program_capabilities repository enforces the same ceiling inside a
    // transaction, so a concurrent edit or a direct API call can't exceed it —
    // this client check just avoids a round-trip for the common case. `excludeId`
    // is the row being edited (empty on create), so a no-op re-save fits.
    const validateEffort = (nextPct, excludeId)=>{
        const othersTotal = items.filter((c)=>c.id !== excludeId).reduce((sum, c)=>sum + (c.percentage ?? 0), 0);
        const currentTotal = items.reduce((sum, c)=>sum + (c.percentage ?? 0), 0);
        // Ceiling is max(100, current total): a compliant program is held at 100, but
        // one already over 100% (possible before this cap) must still be reducible.
        const ceiling = Math.max(100, currentTotal);
        if (othersTotal + nextPct > ceiling + 1e-9) {
            const remaining = Math.max(0, ceiling - othersTotal);
            const asPct = (n)=>Math.round(n * 100) / 100;
            throw new Error(`Capabilities can total at most 100% of effort. This program already allocates ${asPct(othersTotal)}%, so this capability can be at most ${asPct(remaining)}%.`);
        }
    };
    // Add-dialog submit: picks (or inline-creates) a registry capability, then
    // attaches it to the program. Create-only — edits go through the drawer.
    const handleSubmit = async ()=>{
        const categoryName = isNewCapability ? form.newName.trim() : form.capability;
        if (!categoryName) {
            throw new Error('Pick a capability or add a new one.');
        }
        // The percentage is a required 0–100 value; a plain controlled input can't
        // enforce that, so validate before writing rather than letting an empty or
        // negative field silently persist as 0%.
        const nextPct = Number(form.percentage);
        if (form.percentage.trim() === '' || Number.isNaN(nextPct) || nextPct < 0 || nextPct > 100) {
            throw new Error('Percentage must be a number between 0 and 100.');
        }
        validateEffort(nextPct, '');
        // Change #2: a new capability is written to the registry first, then attached
        // to the program — the same create-then-link sequence contacts uses. The ref
        // makes a retry after a failed attach reuse the created row instead of
        // duplicating it. If the attach fails and the user abandons the dialog, the
        // created catalog entry persists unattached — an accepted side effect, since a
        // registry entry is valid standalone (same as the contacts flow).
        if (isNewCapability) {
            const created = await capabilityUpsert.mutateAsync({
                id: createdCapabilityIdRef.current,
                companyId: tenantId,
                name: categoryName,
                description: form.description.trim()
            });
            const newId = created?.id ?? '';
            // Guard the retry-idempotency ref: without an id a failed program attach
            // would create a second registry row on retry instead of reusing this one.
            if (!newId) {
                throw new Error('Creating the capability did not return an id.');
            }
            createdCapabilityIdRef.current = newId;
        }
        await upsert.mutateAsync({
            id: '',
            programId,
            category: categoryName,
            percentage: nextPct,
            description: form.description.trim()
        });
        setFormOpen(false);
        setForm(EMPTY_FORM);
        createdCapabilityIdRef.current = '';
    };
    // Drawer edit-in-place submit: re-keys an existing program capability's
    // category/percentage/description under the same 100%-effort ceiling.
    const submitCapEdit = async (values, id)=>{
        const categoryName = String(values.category ?? '').trim();
        if (!categoryName) {
            throw new Error('Category is required.');
        }
        const nextPct = Number(values.percentage);
        if (Number.isNaN(nextPct) || nextPct < 0 || nextPct > 100) {
            throw new Error('Percentage must be a number between 0 and 100.');
        }
        validateEffort(nextPct, id);
        await upsert.mutateAsync({
            id,
            programId,
            category: categoryName,
            percentage: nextPct,
            description: String(values.description ?? '').trim()
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    const busy = upsert.isPending || capabilityUpsert.isPending;
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: capEditDefaults(detail),
        onSubmit: (v)=>submitCapEdit(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: openAdd,
                    disabled: !canWrite,
                    title: !canWrite ? PROGRAM_WRITE_DENIED.roster : undefined,
                    "data-testid": Testids.deliveryProgramRel.capabilityCreate,
                    children: "Add Capability"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryProgramRel.capabilitiesTable,
                rowTestid: Testids.deliveryProgramRel.capabilityRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                hideRowActions: !canWrite,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryProgramRel.capabilityEdit,
                                children: "Edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryProgramRel.capabilityDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Add Capability",
                fields: [],
                defaultValues: {},
                onSubmit: handleSubmit,
                isSubmitting: busy,
                topContent: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(LabeledField, {
                            id: "capability",
                            label: "Capability",
                            required: true,
                            children: /*#__PURE__*/ _jsxs(Select, {
                                value: form.capability,
                                onValueChange: selectCapability,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        id: "capability",
                                        "data-testid": Testids.deliveryProgramRel.capabilityPicker,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Select a capability…"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NEW_CAPABILITY,
                                                children: "+ Add New Capability…"
                                            }),
                                            registry.map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: c.name,
                                                    children: c.name
                                                }, c.id))
                                        ]
                                    })
                                ]
                            })
                        }),
                        isNewCapability && /*#__PURE__*/ _jsx(LabeledField, {
                            id: "newCapabilityName",
                            label: "New capability name",
                            required: true,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "newCapabilityName",
                                value: form.newName,
                                maxLength: 50,
                                placeholder: "e.g. AI/ML",
                                onChange: (e)=>setField('newName', e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(LabeledField, {
                            id: "capabilityPercentage",
                            label: "Percentage (0-100)",
                            required: true,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "capabilityPercentage",
                                type: "number",
                                min: 0,
                                max: 100,
                                step: 1,
                                value: form.percentage,
                                onChange: (e)=>setField('percentage', e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(LabeledField, {
                            id: "capabilityDescription",
                            label: "Description",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                id: "capabilityDescription",
                                rows: 3,
                                value: form.description,
                                onChange: (e)=>setField('description', e.target.value)
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Remove Capability",
                description: deleting ? `Remove "${deleting.category}" capability from this program? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.category || 'Capability' : '',
                sections: detail ? capabilityDetailSections(detail) : [],
                testid: Testids.deliveryProgramRel.capabilityDetailDrawer,
                edit: detailEdit,
                canEdit: canWrite,
                initialMode: detailMode
            })
        ]
    });
}
